import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useToast } from '../components/Toast';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { RootState } from '../store';
import { apiClient, getApiUrl } from '../utils/api';
import { DateInput } from '../components/DateInput';

type CalendarEvent = {
  id: string;
  type: 'APPOINTMENT' | 'ACTIVITY' | 'VENDOR_VISIT' | 'OTHER';
  status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
  title: string;
  description?: string | null;
  startDateTime: string;
  endDateTime: string;
  timezone?: string | null;
  shopId?: string | null;
  companyId?: string | null;
  practitionerId?: string | null;
  employeeId?: string | null;
  vendorId?: string | null;
  appointmentId?: string | null;
  activityId?: string | null;
  vendorVisitId?: string | null;
  actId?: string | null;
};

export function CalendarEventsPage() {
  const { t, i18n } = useTranslation();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [shops, setShops] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHardDeleting, setIsHardDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; ids: string[] }>({
    isOpen: false,
    ids: [],
  });

  const [selectedShopId, setSelectedShopId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });
  const role = String((user as any)?.role || '').toLowerCase().replace(/-/g, '_');
  const canDelete =
    role === 'super_admin' ||
    role === 'admin' ||
    role === 'company_admin' ||
    role === 'shop_admin' ||
    role === 'manager';
  const canHardDelete = role === 'super_admin';
  const isBusy = loading || isDeleting || isHardDeleting;

  useEffect(() => {
    loadShops();
  }, []);

  useEffect(() => {
    loadEvents();
  }, [selectedShopId, typeFilter, statusFilter, fromDate, toDate]);

  const loadShops = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/shops?limit=1000'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setShops((data.data?.shops || []).map((shop: any) => ({ id: shop.id, name: shop.name })));
      }
    } catch (err) {
      console.error('Failed to load shops:', err);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const fromISO = new Date(`${fromDate}T00:00:00`).toISOString();
      const toISO = new Date(`${toDate}T23:59:59`).toISOString();
      const params = new URLSearchParams({
        from: fromISO,
        to: toISO,
        ...(selectedShopId && { shopId: selectedShopId }),
        ...(typeFilter && { type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
      });
      const response = await fetch(getApiUrl(`/api/planning/events?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.data?.events || []);
      } else {
        setError(data.message || 'Failed to load events');
      }
    } catch (err) {
      console.error('Failed to load events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const shopLabelById = useMemo(() => {
    return new Map(shops.map((shop) => [shop.id, shop.name]));
  }, [shops]);
  const visibleIds = useMemo(() => events.map((event) => event.id), [events]);
  const {
    selectedIds,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);
  const selectedEvents = useMemo(
    () => events.filter((event) => selectedIds.has(event.id)),
    [events, selectedIds],
  );
  const canHardDeleteSelection =
    selectedCount > 0 &&
    selectedEvents.every(
      (event) =>
        (event.type === 'APPOINTMENT' && !!event.appointmentId) ||
        (event.type === 'ACTIVITY' && !!event.activityId),
    );

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return date.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const ownerLabel = (event: CalendarEvent) => {
    if (event.practitionerId) {
      return `${t('planning.eventsPage.owners.practitioner')}: ${event.practitionerId}`;
    }
    if (event.employeeId) {
      return `${t('planning.eventsPage.owners.employee')}: ${event.employeeId}`;
    }
    if (event.vendorId) {
      return `${t('planning.eventsPage.owners.vendor')}: ${event.vendorId}`;
    }
    return '-';
  };

  const assertSuccessfulResponse = async (response: Response, fallbackMessage: string) => {
    const data = await response.json().catch(() => null);
    if (!response.ok || !data?.success) {
      throw new Error(data?.message || data?.error || fallbackMessage);
    }
  };

  const deleteEventEntry = async (event: CalendarEvent, hardDelete = false) => {
    if (event.type === 'APPOINTMENT' && event.appointmentId) {
      const response = await apiClient(
        `/api/admin/appointments/${event.appointmentId}${hardDelete ? '?hardDelete=true' : ''}`,
        { method: 'DELETE' },
      );
      await assertSuccessfulResponse(
        response,
        hardDelete
          ? t('planning.eventsPage.toast.hardDeleteError')
          : t('planning.eventsPage.toast.deleteError'),
      );
      return;
    }

    if (event.type === 'ACTIVITY' && event.activityId) {
      const response = await apiClient(
        `/api/planning/activities/${event.activityId}${hardDelete ? '?hardDelete=true' : ''}`,
        { method: 'DELETE' },
      );
      await assertSuccessfulResponse(
        response,
        hardDelete
          ? t('planning.eventsPage.toast.hardDeleteError')
          : t('planning.eventsPage.toast.deleteError'),
      );
      return;
    }

    if (hardDelete) {
      throw new Error(t('planning.eventsPage.toast.hardDeleteUnsupported'));
    }

    const response = await apiClient(`/api/planning/events/${event.id}`, { method: 'DELETE' });
    await assertSuccessfulResponse(response, t('planning.eventsPage.toast.deleteError'));
  };

  const openBulkDeleteConfirm = () => {
    if (!canDelete || selectedCount === 0) return;
    setBulkDeleteConfirm({ isOpen: true, ids: Array.from(selectedIds) });
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.ids.length === 0) return;
    const eventsToDelete = events.filter((event) => bulkDeleteConfirm.ids.includes(event.id));
    if (eventsToDelete.length === 0) return;

    setIsDeleting(true);
    setError(null);
    const failed: Array<{ id: string; message: string }> = [];

    try {
      for (const event of eventsToDelete) {
        try {
          await deleteEventEntry(event, false);
        } catch (err: any) {
          failed.push({ id: event.id, message: err?.message || t('planning.eventsPage.toast.deleteError') });
        }
      }

      const deletedCount = eventsToDelete.length - failed.length;
      setBulkDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await loadEvents();

      if (failed.length === 0) {
        showToast(t('planning.eventsPage.toast.bulkDeleted_other', { count: deletedCount }), 'success');
      } else if (deletedCount === 0) {
        const msg = t('planning.eventsPage.toast.bulkFailed', { failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = t('planning.eventsPage.toast.bulkPartial', { deleted: deletedCount, failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || !canHardDeleteSelection) return;
    setBulkHardDeleteConfirm({ isOpen: true, ids: Array.from(selectedIds) });
  };

  const confirmBulkHardDelete = async () => {
    if (bulkHardDeleteConfirm.ids.length === 0) return;
    const eventsToDelete = events.filter((event) => bulkHardDeleteConfirm.ids.includes(event.id));
    if (eventsToDelete.length === 0) return;

    setIsHardDeleting(true);
    setError(null);
    const failed: Array<{ id: string; message: string }> = [];

    try {
      for (const event of eventsToDelete) {
        try {
          await deleteEventEntry(event, true);
        } catch (err: any) {
          failed.push({ id: event.id, message: err?.message || t('planning.eventsPage.toast.hardDeleteError') });
        }
      }

      const deletedCount = eventsToDelete.length - failed.length;
      setBulkHardDeleteConfirm({ isOpen: false, ids: [] });
      clearSelection();
      await loadEvents();

      if (failed.length === 0) {
        showToast(t('planning.eventsPage.toast.bulkHardDeleted_other', { count: deletedCount }), 'success');
      } else if (deletedCount === 0) {
        const msg = t('planning.eventsPage.toast.bulkHardFailed', { failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      } else {
        const msg = t('planning.eventsPage.toast.bulkHardPartial', { deleted: deletedCount, failed: failed.length });
        setError(msg);
        showToast(msg, 'error', 7000);
      }
    } finally {
      setIsHardDeleting(false);
    }
  };

  return (
    <div className={`space-y-6 ${isBusy ? 'cursor-wait' : ''}`}>
      <ToastComponent />
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('planning.eventsPage.title')}
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          {t('planning.eventsPage.subtitle')}
        </p>
      </div>

      {selectedCount > 0 && canDelete && (
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={openBulkDeleteConfirm}
            disabled={isDeleting || isHardDeleting}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2 text-sm font-medium"
          >
            <Trash2 className="w-4 h-4" />
            {t('planning.eventsPage.bulk.delete', { count: selectedCount })}
          </button>
          {canHardDelete && (
            <button
              onClick={openBulkHardDeleteConfirm}
              disabled={isDeleting || isHardDeleting || !canHardDeleteSelection}
              title={!canHardDeleteSelection ? t('planning.eventsPage.bulk.hardDeleteHint') : undefined}
              className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center gap-2 text-sm font-medium"
            >
              <AlertCircle className="w-4 h-4" />
              {t('planning.eventsPage.bulk.hardDelete', { count: selectedCount })}
            </button>
          )}
          <span className="text-sm text-gray-600">
            {t('planning.eventsPage.bulk.selected_other', { count: selectedCount })}
          </span>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="text-sm font-semibold text-gray-700">
          {t('planning.eventsPage.filtersTitle')}
        </div>
        <div className="dt-filters grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('planning.eventsPage.shop')}
            </label>
            <select
              value={selectedShopId}
              onChange={(e) => setSelectedShopId(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('planning.eventsPage.allShops')}</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('planning.eventsPage.type')}
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('planning.eventsPage.type')}</option>
              {['APPOINTMENT', 'ACTIVITY', 'VENDOR_VISIT', 'OTHER'].map((type) => (
                <option key={type} value={type}>
                  {t(`planning.eventsPage.types.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('planning.eventsPage.status')}
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('planning.eventsPage.status')}</option>
              {['PLANNED', 'COMPLETED', 'CANCELLED'].map((status) => (
                <option key={status} value={status}>
                  {t(`planning.eventsPage.statuses.${status}`)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('planning.eventsPage.fromDate')}
            </label>
            <DateInput
              value={fromDate}
              onChange={(value) => setFromDate(value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {t('planning.eventsPage.toDate')}
            </label>
            <DateInput
              value={toDate}
              onChange={(value) => setToDate(value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {error && !loading ? (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">{t('planning.eventsPage.loading')}</div>
        ) : events.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">{t('planning.eventsPage.empty')}</div>
        ) : (
          <TableScrollContainer hint={t('planning.eventsPage.scrollHint')}>
            <ResponsiveTable className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      disabled={events.length === 0 || isDeleting || isHardDeleting}
                      onChange={toggleSelectAllVisible}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      aria-label={t('planning.eventsPage.bulk.selectAll')}
                    />
                  </th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.type')}</th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.title')}</th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.time')}</th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.owner')}</th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.shop')}</th>
                  <th className="px-4 py-3 text-left">{t('planning.eventsPage.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-t border-gray-100">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(event.id)}
                        disabled={isDeleting || isHardDeleting}
                        onChange={() => toggleSelect(event.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        aria-label={t('planning.eventsPage.bulk.selectRow', { title: event.title })}
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      {t(`planning.eventsPage.types.${event.type}`)}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{event.title}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDateTime(event.startDateTime)} → {formatDateTime(event.endDateTime)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{ownerLabel(event)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {event.shopId ? shopLabelById.get(event.shopId) || event.shopId : '-'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {t(`planning.eventsPage.statuses.${event.status}`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </TableScrollContainer>
        )}
      </div>

      {bulkDeleteConfirm.isOpen && (
        <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isDeleting ? 'cursor-wait' : ''}`}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">{t('planning.eventsPage.confirm.deleteTitle')}</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {t('planning.eventsPage.confirm.bulkDeleteMessage', { count: bulkDeleteConfirm.ids.length })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBulkDeleteConfirm({ isOpen: false, ids: [] })}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                {t('planning.eventsPage.confirm.cancel')}
              </button>
              <button
                onClick={() => void confirmBulkDelete()}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm inline-flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('planning.eventsPage.confirm.deleting')}
                  </>
                ) : (
                  t('planning.eventsPage.confirm.confirm')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={() => setBulkHardDeleteConfirm({ isOpen: false, ids: [] })}
        entityLabel="evenement(s)"
        count={bulkHardDeleteConfirm.ids.length}
        isLoading={isHardDeleting}
      />
    </div>
  );
}
